'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useWishlist } from '@/contexts/WishlistContext';
import { useCart } from '@/contexts/CartContext';
import { isAuthenticated, clearAuthToken, clearAuthUser, getAuthUser, type AuthUser } from '@/lib/apollo-client-simple';
import { useQuery } from '@apollo/client';
import { GET_CUSTOMER } from '@/lib/graphql-queries';

type Tab = 'overview' | 'orders' | 'wishlist' | 'addresses' | 'settings';

const BRAND_COLLAGE_IMAGE = '/assets/images/kym-products/kym-jar-07.jpg';

// Email preferences — state is local only for now (no backend persistence yet).
// Button click shows a transient "Preferences saved." confirmation so the UI
// feels responsive; when the backend endpoint lands, swap the setTimeout for
// a real POST + server-returned success state.
function EmailPreferences() {
  const initial = [
    { key: 'orders',      label: 'Order updates & shipping notifications', checked: true },
    { key: 'launches',    label: 'New product launches & collections',     checked: true },
    { key: 'promotions',  label: 'Exclusive offers & promotions',          checked: false },
    { key: 'editorial',   label: 'Beauty tips & editorial content',        checked: false },
  ];
  const [prefs, setPrefs] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(initial.map((p) => [p.key, p.checked]))
  );
  const [status, setStatus] = useState<{ kind: 'ok' | 'saving' | null; text: string }>({ kind: null, text: '' });

  const handleSave = () => {
    setStatus({ kind: 'saving', text: 'Saving…' });
    // TODO: replace with real POST to /wp-json/nexbeauty/v1/update-email-prefs
    // once the merchant confirms backend persistence is wanted. Using a short
    // timeout to simulate an async call so the spinner behaviour reads right.
    setTimeout(() => {
      setStatus({ kind: 'ok', text: 'Preferences saved.' });
      setTimeout(() => setStatus({ kind: null, text: '' }), 3000);
    }, 300);
  };

  return (
    <div>
      <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-5">Email Preferences</h3>
      <div className="space-y-4">
        {initial.map((pref) => (
          <label key={pref.key} className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={prefs[pref.key]}
              onChange={(e) => setPrefs((p) => ({ ...p, [pref.key]: e.target.checked }))}
              className="w-4 h-4 accent-accent" />
            <span className="text-sm text-foreground">{pref.label}</span>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-6">
        <button
          type="button"
          onClick={handleSave}
          disabled={status.kind === 'saving'}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
          {status.kind === 'saving' ? 'Saving…' : 'Save Preferences'}
        </button>
        {status.kind === 'ok' &&
        <span className="text-[12px] text-green-700 flex items-center gap-1.5">
            <Icon name="CheckCircleIcon" size={14} className="text-green-700" />
            {status.text}
          </span>
        }
      </div>
    </div>
  );
}

// Inline address form used for both billing + shipping editing. Jamaica-first
// (parish dropdown when country=JM), falls back to free-text for other countries.
type Addr = { firstName: string; lastName: string; company: string; address1: string; address2: string; city: string; state: string; postcode: string; country: string; email: string; phone: string };
function AddressForm({ value, onChange, includeContact, parishes, onSave, onCancel, saving }: {
  value: Addr; onChange: (a: Addr) => void; includeContact: boolean; parishes: string[];
  onSave: () => void; onCancel: () => void; saving: boolean;
}) {
  const set = (k: keyof Addr, v: string) => onChange({ ...value, [k]: v });
  const lbl = 'block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-1.5';
  const inp = 'w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring';
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>First Name <span className="text-red-600">*</span></label>
          <input className={inp} value={value.firstName} onChange={(e) => set('firstName', e.target.value)} />
        </div>
        <div>
          <label className={lbl}>Last Name <span className="text-red-600">*</span></label>
          <input className={inp} value={value.lastName} onChange={(e) => set('lastName', e.target.value)} />
        </div>
      </div>
      <div>
        <label className={lbl}>Street Address <span className="text-red-600">*</span></label>
        <input className={inp} value={value.address1} onChange={(e) => set('address1', e.target.value)} />
      </div>
      <div>
        <label className={lbl}>Apt / Suite (optional)</label>
        <input className={inp} value={value.address2} onChange={(e) => set('address2', e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>City / Town <span className="text-red-600">*</span></label>
          <input className={inp} value={value.city} onChange={(e) => set('city', e.target.value)} />
        </div>
        <div>
          <label className={lbl}>{value.country === 'JM' ? 'Parish' : 'State / Province'} <span className="text-red-600">*</span></label>
          {value.country === 'JM' ? (
            <select className={inp} value={value.state} onChange={(e) => set('state', e.target.value)}>
              <option value="">Select a parish</option>
              {parishes.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          ) : (
            <input className={inp} value={value.state} onChange={(e) => set('state', e.target.value)} />
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Country</label>
          <select className={inp} value={value.country} onChange={(e) => set('country', e.target.value)}>
            <option value="JM">Jamaica</option>
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="GB">United Kingdom</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Postal Code (optional)</label>
          <input className={inp} value={value.postcode} onChange={(e) => set('postcode', e.target.value)} />
        </div>
      </div>
      {includeContact && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Email</label>
            <input className={inp} type="email" value={value.email} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Phone</label>
            <input className={inp} type="tel" value={value.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <button disabled={saving} onClick={onSave} className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-60">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button disabled={saving} onClick={onCancel} className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider border border-border hover:bg-secondary transition-colors disabled:opacity-60">
          Cancel
        </button>
      </div>
    </div>
  );
}

function BrandAvatar({ size = 56 }: { size?: number }) {
  return (
    <div className="relative w-full h-full overflow-hidden bg-background">
      <AppImage
        src={BRAND_COLLAGE_IMAGE}
        alt="SweetSkyn body butter collection"
        fill
        sizes={`${size}px`}
        className="object-cover blur-[1.5px] scale-110" />
    </div>
  );
}

const addresses: Array<{ id: string; label: string; name: string; line1: string; line2: string; default: boolean }> = [];


const navItems: {id: Tab;label: string;icon: string;}[] = [
{ id: 'overview', label: 'Overview', icon: 'HomeIcon' },
{ id: 'orders', label: 'Orders', icon: 'ShoppingBagIcon' },
{ id: 'wishlist', label: 'Wishlist', icon: 'HeartIcon' },
{ id: 'addresses', label: 'Addresses', icon: 'MapPinIcon' },
{ id: 'settings', label: 'Settings', icon: 'Cog6ToothIcon' }];


const statusColors: Record<string, string> = {
  'delivered': 'bg-green-50 text-green-700',
  'completed': 'bg-green-50 text-green-700',
  'processing': 'bg-amber-50 text-amber-700',
  'shipped': 'bg-blue-50 text-blue-700',
  'on hold': 'bg-amber-50 text-amber-700',
  'pending': 'bg-slate-100 text-slate-700',
  'cancelled': 'bg-red-50 text-red-700',
  'refunded': 'bg-rose-50 text-rose-700',
};

export default function AccountClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const validTabs: Tab[] = ['overview', 'orders', 'wishlist', 'addresses', 'settings'];
  const tabParam = searchParams?.get('tab') as Tab | null;
  const initialTab: Tab = tabParam && validTabs.includes(tabParam) ? tabParam : 'overview';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { items: wishlistCtxItems, removeFromWishlist: removeFromWishlistCtx } = useWishlist();
  const { addToCart } = useCart();
  const wishlist = wishlistCtxItems.map((p: any) => String(p.id));

  const [authUser, setAuthUserState] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Fetch WC customer (billing/shipping addresses) — beauty-store pattern.
  // Only runs when authenticated. Apollo's authLink attaches the Bearer token.
  const { data: customerData } = useQuery(GET_CUSTOMER, {
    skip: !authChecked || !isAuthenticated(),
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const billing = customerData?.customer?.billing;
  const shipping = customerData?.customer?.shipping;

  // Real orders — beauty-store pattern. Transform GraphQL nodes into the UI
  // shape already consumed by the Orders tab render block.
  const orders = (customerData?.customer?.orders?.nodes || []).map((o: any) => {
    const lineItemNodes = o?.lineItems?.nodes || [];
    const firstItem = lineItemNodes[0];
    const imageSrc = firstItem?.product?.node?.image?.sourceUrl || '/assets/images/kym-products/kym-jar-07.jpg';
    const imageAlt = firstItem?.product?.node?.image?.altText || 'SweetSkyn order';
    const itemCount = lineItemNodes.reduce((sum: number, li: any) => sum + (li.quantity || 0), 0);
    return {
      id: String(o?.databaseId || o?.id || ''),
      date: o?.date ? new Date(o.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '',
      status: o?.status ? String(o.status).replace(/^ORDER_STATUS_/i, '').toLowerCase().replace(/_/g, ' ') : 'on hold',
      total: parseFloat(String(o?.total || '0').replace(/[^0-9.]/g, '')) || 0,
      items: itemCount,
      image: imageSrc,
      alt: imageAlt,
      lineItems: lineItemNodes.map((li: any) => ({
        name: li?.product?.node?.name || 'Product',
        image: li?.product?.node?.image?.sourceUrl || '/assets/images/kym-products/kym-jar-07.jpg',
        alt: li?.product?.node?.image?.altText || li?.product?.node?.name || 'Product',
        quantity: li?.quantity || 1,
        total: parseFloat(String(li?.total || '0').replace(/[^0-9.]/g, '')) || 0,
      })),
    };
  });

  // Tracks which order row is expanded to show its line items inline.
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Addresses — editable billing + shipping. Prefilled from customerData once loaded.
  const JAMAICA_PARISHES = [
    'Kingston', 'St. Andrew', 'St. Catherine', 'Clarendon', 'Manchester',
    'St. Elizabeth', 'Westmoreland', 'Hanover', 'St. James', 'Trelawny',
    'St. Ann', 'St. Mary', 'Portland', 'St. Thomas',
  ];
  const emptyAddress = { firstName: '', lastName: '', company: '', address1: '', address2: '', city: '', state: '', postcode: '', country: 'JM', email: '', phone: '' };
  const [billingForm, setBillingForm] = useState<typeof emptyAddress>({ ...emptyAddress });
  const [shippingForm, setShippingForm] = useState<typeof emptyAddress>({ ...emptyAddress });
  const [editingBilling, setEditingBilling] = useState(false);
  const [editingShipping, setEditingShipping] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressMessage, setAddressMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Sync forms when customer data arrives.
  useEffect(() => {
    if (billing) setBillingForm({ ...emptyAddress, ...billing });
    if (shipping) setShippingForm({ ...emptyAddress, ...shipping });
    // Clear any stale status message once fresh data lands.
    setAddressMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerData?.customer?.billing, customerData?.customer?.shipping]);

  const saveAddress = async (which: 'billing' | 'shipping') => {
    setAddressSaving(true);
    setAddressMessage(null);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : null;
      if (!token) throw new Error('Please sign in again.');
      const body: any = {};
      body[which] = which === 'billing' ? billingForm : shippingForm;
      const WP_URL = process.env.NEXT_PUBLIC_WORDPRESS_URL || '';
      const res = await fetch(`${WP_URL}/wp-json/nexbeauty/v1/update-address`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to save address.');
      setAddressMessage({ kind: 'ok', text: `${which === 'billing' ? 'Billing' : 'Shipping'} address saved.` });
      if (which === 'billing') setEditingBilling(false);
      else setEditingShipping(false);
    } catch (err: any) {
      setAddressMessage({ kind: 'err', text: err?.message || 'Something went wrong.' });
    } finally {
      setAddressSaving(false);
    }
  };

  // Profile + password forms (settings tab). Values default from authUser
  // on load; user edits then clicks Save to POST to the nexbeauty theme's
  // /update-profile and /change-password REST routes.
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', email: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  const [passwordVisible, setPasswordVisible] = useState<{ current: boolean; next: boolean; confirm: boolean }>({ current: false, next: false, confirm: false });

  // Seed form values from the auth user + customer GraphQL data on first load.
  useEffect(() => {
    if (!authUser) return;
    setProfileForm((prev) => ({
      firstName: prev.firstName || authUser.firstName || '',
      lastName: prev.lastName || authUser.lastName || '',
      email: prev.email || authUser.email || '',
    }));
  }, [authUser]);

  const WORDPRESS_URL = (typeof window !== 'undefined' && (process.env.NEXT_PUBLIC_WORDPRESS_URL as string)) || '';

  const handleSaveProfile = async () => {
    setProfileMsg(null);
    setProfileSaving(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : '';
      const res = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          first_name: profileForm.firstName,
          last_name: profileForm.lastName,
          email: profileForm.email,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && (json.success === undefined || json.success)) {
        setProfileMsg('Profile updated.');
      } else {
        setProfileMsg(json.message || `Update failed (${res.status}).`);
      }
    } catch (err: any) {
      setProfileMsg(err?.message || 'Network error — try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordMsg(null);
    if (passwordForm.next.length < 6) {
      setPasswordMsg('New password must be at least 6 characters.');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordMsg('New password and confirmation don\'t match.');
      return;
    }
    setPasswordSaving(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth-token') : '';
      const res = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.next,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && (json.success === undefined || json.success)) {
        setPasswordMsg('Password updated.');
        setPasswordForm({ current: '', next: '', confirm: '' });
      } else {
        setPasswordMsg(json.message || `Update failed (${res.status}).`);
      }
    } catch (err: any) {
      setPasswordMsg(err?.message || 'Network error — try again.');
    } finally {
      setPasswordSaving(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      router.push('/sign-up-login?redirect=/account');
      return;
    }
    setAuthUserState(getAuthUser());
    setAuthChecked(true);
  }, [router]);

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const removeFromWishlist = (id: string | number) => {
    removeFromWishlistCtx(typeof id === 'string' ? (parseInt(id) || 0) : id);
  };

  const handleLogout = () => {
    clearAuthToken();
    clearAuthUser();
    router.push('/');
  };

  const user = {
    name: authUser?.displayName || [authUser?.firstName, authUser?.lastName].filter(Boolean).join(' ') || authUser?.username || 'Friend',
    email: authUser?.email || '',
    memberSince: '',
    avatar: authUser?.avatar || '',
  };

  if (!authChecked) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 pb-20 pt-4">
        <span className="section-label block mb-2">My Account</span>
        <p className="text-sm text-muted-foreground">Checking your session…</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pb-20 pt-4">
      {/* Page header */}
      <div className="mb-8">
        <span className="section-label block mb-2">My Account</span>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl md:text-4xl font-light text-foreground">
            Welcome back, {user.name.split(' ')[0]}
          </h1>
          <Link
            href="/sign-up-login"
            className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
            
            <Icon name="ArrowRightOnRectangleIcon" size={16} />
            Sign Out
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          {/* User card */}
          <div className="bg-secondary p-6 card-rounded mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-sm">
                {user.avatar ? (
                  <AppImage
                    src={user.avatar}
                    alt={`${user.name} profile photo`}
                    width={56}
                    height={56}
                    className="object-cover w-full h-full" />
                ) : (
                  <BrandAvatar size={56} />
                )}
              </div>
              <div>
                <p className="font-semibold text-sm text-foreground">{user.name}</p>
                <p className="text-[11px] text-muted-foreground">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              type="button"
              className="w-full text-[10px] font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground border-t border-border pt-3 mt-1 flex items-center justify-center gap-1.5 transition-colors">
              <Icon name="ArrowRightOnRectangleIcon" size={12} />
              Sign out
            </button>
          </div>

          {/* Mobile nav toggle */}
          <button
            className="lg:hidden w-full flex items-center justify-between border border-border px-4 py-3 mb-4 text-sm font-semibold"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}>
            
            <span>{navItems.find((n) => n.id === activeTab)?.label}</span>
            <Icon name={mobileNavOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} />
          </button>

          {/* Nav */}
          <nav className={`space-y-1 ${mobileNavOpen ? 'block' : 'hidden lg:block'}`}>
            {navItems.map((item) =>
            <button
              key={item.id}
              onClick={() => {setActiveTab(item.id);setMobileNavOpen(false);}}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200 text-left ${
              activeTab === item.id ?
              'bg-primary text-primary-foreground' :
              'text-foreground hover:bg-secondary'}`
              }>
              
                <Icon name={item.icon as 'HomeIcon'} size={18} />
                {item.label}
                {item.id === 'orders' &&
              <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${activeTab === item.id ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                    {orders.length}
                  </span>
              }
                {item.id === 'wishlist' &&
              <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${activeTab === item.id ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                    {wishlist.length}
                  </span>
              }
              </button>
            )}
          </nav>
        </aside>

        {/* Main content */}
        <div className="lg:col-span-9">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' &&
          <div className="space-y-8">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {([
              { label: 'Total Orders', value: orders.length.toString(), icon: 'ShoppingBagIcon' as const },
              { label: 'Wishlist Items', value: wishlist.length.toString(), icon: 'HeartIcon' as const },
              { label: 'Total Spent', value: (<><span className="font-sans">J$</span>{orders.reduce((s, o) => s + o.total, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>), icon: 'CreditCardIcon' as const }
              ] as Array<{ label: string; value: React.ReactNode; icon: string }>).
              map((stat) =>
              <div key={stat.label} className="bg-secondary p-5 card-rounded">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Icon name={stat.icon as 'ShoppingBagIcon'} size={18} className="text-accent" />
                      </div>
                    </div>
                    <p className="font-display text-2xl font-light text-foreground">{stat.value}</p>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{stat.label}</p>
                  </div>
              )}
              </div>

              {/* Recent orders */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-light text-foreground">Recent Orders</h2>
                  <button onClick={() => setActiveTab('orders')} className="text-[11px] font-semibold uppercase tracking-widest text-accent hover:underline">
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {orders.length === 0 && (
                    <p className="text-[13px] text-muted-foreground italic">No orders yet. <Link href="/products" className="underline hover:text-foreground">Start shopping</Link>.</p>
                  )}
                  {orders.slice(0, 2).map((order) =>
                <div key={order.id} className="flex items-center gap-4 p-4 bg-secondary card-rounded">
                      <div className="w-12 h-12 overflow-hidden rounded flex-shrink-0">
                        <AppImage src={order.image} alt={order.alt} width={48} height={48} className="object-cover w-full h-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{order.id}</p>
                        <p className="text-[11px] text-muted-foreground">{order.date} · {order.items} item{order.items > 1 ? 's' : ''}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-foreground"><span className="font-sans">J$</span>{order.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[order.status]}`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                )}
                </div>
              </div>

              {/* Saved items preview */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-light text-foreground">Saved Items</h2>
                  <button onClick={() => setActiveTab('wishlist')} className="text-[11px] font-semibold uppercase tracking-widest text-accent hover:underline">
                    View All
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {wishlistCtxItems.slice(0, 3).map((p: any) => {
                    const imgSrc = (Array.isArray(p.images) && p.images[0]?.src) || p.image?.sourceUrl || '/assets/images/kym-products/kym-jar-07.jpg';
                    const imgAlt = (Array.isArray(p.images) && p.images[0]?.alt) || p.name || 'Product';
                    const priceNum = parseFloat(String(p.price || p.regular_price || '0').replace(/[^0-9.]/g, '')) || 0;
                    return (
                      <Link key={p.id} href="/product-detail" className="group">
                        <div className="aspect-square overflow-hidden card-rounded bg-secondary">
                          <AppImage
                            src={imgSrc}
                            alt={imgAlt}
                            fill
                            sizes="(max-width: 768px) 33vw, 200px"
                            className="object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <p className="text-[11px] font-medium text-foreground mt-2 truncate">{p.name}</p>
                        <p className="text-[11px] text-muted-foreground"><span className="font-sans">J$</span>{priceNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </Link>
                    );
                  })}
                  {wishlistCtxItems.length === 0 && (
                    <p className="col-span-3 text-[12px] text-muted-foreground italic py-4">No saved items yet. <Link href="/products" className="underline hover:text-foreground">Browse products</Link>.</p>
                  )}
                </div>
              </div>
            </div>
          }

          {/* ORDERS TAB */}
          {activeTab === 'orders' &&
          <div>
              <h2 className="font-display text-2xl font-light text-foreground mb-6">Order History</h2>
              <div className="space-y-4">
                {orders.length === 0 && (
                  <div className="text-center py-16">
                    <Icon name="ShoppingBagIcon" size={48} className="text-muted mx-auto mb-4" />
                    <p className="font-display text-2xl font-light text-muted-foreground mb-4">You haven't placed an order yet</p>
                    <Link href="/products" className="btn-primary">Browse Products</Link>
                  </div>
                )}
                {orders.map((order) =>
              <div key={order.id} className="border border-border card-rounded overflow-hidden">
                    {/* Order header */}
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-secondary border-b border-border">
                      <div className="flex flex-wrap gap-6">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Order</p>
                          <p className="text-sm font-semibold text-foreground">{order.id}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Date</p>
                          <p className="text-sm font-semibold text-foreground">{order.date}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Total</p>
                          <p className="text-sm font-semibold text-foreground"><span className="font-sans">J$</span>{order.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider ${statusColors[order.status]}`}>
                        {order.status}
                      </span>
                    </div>

                    {/* Order body */}
                    <div className="px-5 py-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 overflow-hidden rounded flex-shrink-0">
                          <AppImage src={order.image} alt={order.alt} width={56} height={56} className="object-cover w-full h-full" />
                        </div>
                        <div>
                          <p className="text-sm text-foreground">{order.items} item{order.items > 1 ? 's' : ''}</p>
                          <p className="text-[11px] text-muted-foreground">Free shipping</p>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                          className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider border border-border hover:bg-secondary transition-colors">
                          {expandedOrderId === order.id ? 'Hide Details' : 'View Details'}
                        </button>
                        {(order.status === 'completed' || order.status === 'delivered') &&
                    <button className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
                            Reorder
                          </button>
                    }
                      </div>
                    </div>

                    {/* Expanded line items */}
                    {expandedOrderId === order.id &&
                    <div className="px-5 py-4 bg-secondary/40 border-t border-border space-y-3">
                        {order.lineItems.length === 0 &&
                      <p className="text-[12px] text-muted-foreground">No line items returned for this order.</p>
                      }
                        {order.lineItems.map((li, i) =>
                      <div key={i} className="flex items-center gap-3">
                            <div className="w-10 h-10 overflow-hidden rounded flex-shrink-0">
                              <AppImage src={li.image} alt={li.alt} width={40} height={40} className="object-cover w-full h-full" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground truncate">{li.name}</p>
                              <p className="text-[11px] text-muted-foreground">Qty: {li.quantity}</p>
                            </div>
                            <span className="text-sm font-semibold text-foreground"><span className="font-sans">J$</span>{li.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                      )}
                      </div>
                    }
                  </div>
              )}
              </div>
            </div>
          }

          {/* WISHLIST TAB */}
          {activeTab === 'wishlist' &&
          <div>
              <h2 className="font-display text-2xl font-light text-foreground mb-6">
                Saved Items ({wishlist.length})
              </h2>
              {wishlist.length === 0 ?
            <div className="text-center py-16">
                  <Icon name="HeartIcon" size={48} className="text-muted mx-auto mb-4" />
                  <p className="font-display text-2xl font-light text-muted-foreground mb-4">Your wishlist is empty</p>
                  <Link href="/products" className="btn-primary">Discover Products</Link>
                </div> :

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                  {wishlistCtxItems.map((p: any) => {
                    const imgSrc = (Array.isArray(p.images) && p.images[0]?.src) || p.image?.sourceUrl || '/assets/images/kym-products/kym-jar-07.jpg';
                    const imgAlt = (Array.isArray(p.images) && p.images[0]?.alt) || p.name || 'Product';
                    const catName = (Array.isArray(p.categories) && p.categories[0]?.name) || 'Natural Body Butters';
                    const priceNum = parseFloat(String(p.price || p.regular_price || '0').replace(/[^0-9.]/g, '')) || 0;
                    return (
                      <div key={p.id} className="group">
                        <div className="relative overflow-hidden card-rounded bg-secondary aspect-[3/4] mb-4">
                          <AppImage
                            src={imgSrc}
                            alt={imgAlt}
                            fill
                            sizes="(max-width: 640px) 50vw, 33vw"
                            className="object-cover product-card-img" />

                          <button
                            onClick={() => removeFromWishlist(p.id)}
                            className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full text-accent hover:bg-white transition-colors"
                            aria-label="Remove from wishlist">

                            <Icon name="HeartIcon" size={14} variant="solid" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-spring">
                            <button
                              type="button"
                              onClick={() => addToCart(p, 1)}
                              className="block w-full text-center bg-white text-primary text-[10px] font-semibold uppercase tracking-widest py-3 hover:bg-accent hover:text-accent-foreground transition-colors">
                              Add to Bag
                            </button>
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{catName}</span>
                        <Link href="/product-detail" className="block font-medium text-sm text-foreground mt-1 hover:text-accent transition-colors">
                          {p.name}
                        </Link>
                        <p className="text-sm font-semibold text-foreground mt-1"><span className="font-sans">J$</span>{priceNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    );
                  })}
                </div>
            }
            </div>
          }

          {/* ADDRESSES TAB — billing + shipping editable inline */}
          {activeTab === 'addresses' &&
          <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-light text-foreground">Addresses</h2>
              </div>

              {addressMessage && (
                <div className={`mb-4 p-3 text-[12px] rounded border ${addressMessage.kind === 'ok' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
                  {addressMessage.text}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {/* Billing */}
                <div className="p-5 card-rounded border-2 border-border bg-secondary">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon name="CreditCardIcon" size={14} className="text-accent" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-foreground">Billing Address</span>
                    </div>
                    {!editingBilling && (
                      <button onClick={() => setEditingBilling(true)} className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                        Edit
                      </button>
                    )}
                  </div>

                  {editingBilling ? (
                    <AddressForm
                      value={billingForm}
                      onChange={setBillingForm}
                      includeContact
                      parishes={JAMAICA_PARISHES}
                      onSave={() => saveAddress('billing')}
                      onCancel={() => { setEditingBilling(false); if (billing) setBillingForm({ ...emptyAddress, ...billing }); }}
                      saving={addressSaving}
                    />
                  ) : billingForm.address1 || billingForm.city ? (
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-foreground">{[billingForm.firstName, billingForm.lastName].filter(Boolean).join(' ')}</p>
                      {billingForm.company && <p className="text-muted-foreground">{billingForm.company}</p>}
                      {billingForm.address1 && <p className="text-muted-foreground">{billingForm.address1}</p>}
                      {billingForm.address2 && <p className="text-muted-foreground">{billingForm.address2}</p>}
                      {(billingForm.city || billingForm.state || billingForm.postcode) && (
                        <p className="text-muted-foreground">{[billingForm.city, billingForm.state].filter(Boolean).join(', ')} {billingForm.postcode || ''}</p>
                      )}
                      {billingForm.country && <p className="text-muted-foreground">{billingForm.country}</p>}
                      {billingForm.email && <p className="text-[11px] text-muted-foreground mt-2">{billingForm.email}</p>}
                      {billingForm.phone && <p className="text-[11px] text-muted-foreground">{billingForm.phone}</p>}
                    </div>
                  ) : (
                    <p className="text-[13px] text-muted-foreground italic">No billing address on file. Click Edit to add one.</p>
                  )}
                </div>

                {/* Shipping */}
                <div className="p-5 card-rounded border-2 border-border bg-secondary">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon name="TruckIcon" size={14} className="text-accent" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-foreground">Shipping Address</span>
                    </div>
                    {!editingShipping && (
                      <button onClick={() => setEditingShipping(true)} className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                        Edit
                      </button>
                    )}
                  </div>

                  {editingShipping ? (
                    <AddressForm
                      value={shippingForm}
                      onChange={setShippingForm}
                      includeContact={false}
                      parishes={JAMAICA_PARISHES}
                      onSave={() => saveAddress('shipping')}
                      onCancel={() => { setEditingShipping(false); if (shipping) setShippingForm({ ...emptyAddress, ...shipping }); }}
                      saving={addressSaving}
                    />
                  ) : shippingForm.address1 || shippingForm.city ? (
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-foreground">{[shippingForm.firstName, shippingForm.lastName].filter(Boolean).join(' ')}</p>
                      {shippingForm.company && <p className="text-muted-foreground">{shippingForm.company}</p>}
                      {shippingForm.address1 && <p className="text-muted-foreground">{shippingForm.address1}</p>}
                      {shippingForm.address2 && <p className="text-muted-foreground">{shippingForm.address2}</p>}
                      {(shippingForm.city || shippingForm.state || shippingForm.postcode) && (
                        <p className="text-muted-foreground">{[shippingForm.city, shippingForm.state].filter(Boolean).join(', ')} {shippingForm.postcode || ''}</p>
                      )}
                      {shippingForm.country && <p className="text-muted-foreground">{shippingForm.country}</p>}
                    </div>
                  ) : (
                    <p className="text-[13px] text-muted-foreground italic">No shipping address on file. Click Edit to add one.</p>
                  )}
                </div>
              </div>
            </div>
          }

          {/* SETTINGS TAB */}
          {activeTab === 'settings' &&
          <div>
              <h2 className="font-display text-2xl font-light text-foreground mb-8">Account Settings</h2>

              {/* Profile */}
              <div className="mb-8 pb-8 border-b border-border">
                <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-5">Profile Information</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">First Name</label>
                    <input
                      type="text"
                      value={profileForm.firstName}
                      onChange={(e) => setProfileForm((f) => ({ ...f, firstName: e.target.value }))}
                      placeholder="First name"
                      className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Last Name</label>
                    <input
                      type="text"
                      value={profileForm.lastName}
                      onChange={(e) => setProfileForm((f) => ({ ...f, lastName: e.target.value }))}
                      placeholder="Last name"
                      className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Email Address</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
                </div>
                <button
                  type="button"
                  disabled={profileSaving}
                  onClick={handleSaveProfile}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                  {profileSaving ? 'Saving…' : 'Save Changes'}
                </button>
                {profileMsg && (
                  <p className="text-[12px] text-muted-foreground mt-3">{profileMsg}</p>
                )}
              </div>

              {/* Password */}
              <div className="mb-8 pb-8 border-b border-border">
                <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-5">Change Password</h3>
                <div className="space-y-4 max-w-sm">
                  {([
                    { key: 'current' as const, label: 'Current Password' },
                    { key: 'next' as const, label: 'New Password' },
                    { key: 'confirm' as const, label: 'Confirm New Password' },
                  ]).map((f) => (
                    <div key={f.key}>
                      <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">{f.label}</label>
                      <div className="relative">
                        <input
                          type={passwordVisible[f.key] ? 'text' : 'password'}
                          value={passwordForm[f.key]}
                          onChange={(e) => setPasswordForm((p) => ({ ...p, [f.key]: e.target.value }))}
                          placeholder="••••••••"
                          className="w-full border border-border bg-background px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
                        <button
                          type="button"
                          onClick={() => setPasswordVisible((v) => ({ ...v, [f.key]: !v[f.key] }))}
                          aria-label={passwordVisible[f.key] ? `Hide ${f.label.toLowerCase()}` : `Show ${f.label.toLowerCase()}`}
                          className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-muted-foreground hover:text-foreground transition-colors">
                          <Icon name={passwordVisible[f.key] ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    disabled={passwordSaving}
                    onClick={handleChangePassword}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                    {passwordSaving ? 'Updating…' : 'Update Password'}
                  </button>
                  {passwordMsg && (
                    <p className="text-[12px] text-muted-foreground">{passwordMsg}</p>
                  )}
                </div>
              </div>

              {/* Notifications */}
              <EmailPreferences />
            </div>
          }
        </div>
      </div>
    </div>);

}